import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { leadAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, Filter, Download, Phone, Mail, MapPin, 
  Calendar, ArrowUpRight, Users, ChevronLeft, ChevronRight,
  AlertCircle, Upload, Send, Plus, Loader2, Trash2, Sparkles
} from 'lucide-react';
import { CsvImportWizard } from '@/components/CsvImportWizard';
import { EmailComposeDialog } from '@/components/EmailComposeDialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export function LeadsListPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addForm, setAddForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address: '', city: '',
    source: 'direct', status: 'new', notes: '',
  });

  // Delete action available directly from the list — needed when
  // LeadDetailPage cannot render (e.g. corrupted missed-call lead) and the
  // user has no other way to remove the row.
  const canDeleteLead = Boolean(user?.permissions?.can_edit_leads);
  const [deleteTarget, setDeleteTarget] = useState(null); // { lead_id, name } or null
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [source, setSource] = useState(searchParams.get('source') || 'all');
  // form_type — filters between Standard forms and AI Estimation forms.
  // "all" means no filter, behaves byte-identically to pre-feature behaviour.
  const [formType, setFormType] = useState(searchParams.get('form_type') || 'all');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);

  const STATUS_OPTIONS = [
    { value: 'all', label: t('leads.allStatuses') },
    { value: 'new', label: t('status.new') },
    { value: 'contacted', label: t('status.contacted') },
    { value: 'appointment', label: t('status.appointment') },
    { value: 'quote_sent', label: t('status.quote_sent') },
    { value: 'won', label: t('status.won') },
    { value: 'lost', label: t('status.lost') },
  ];

  const SOURCE_OPTIONS = [
    { value: 'all', label: t('leads.allSources') },
    { value: 'maps', label: t('source.maps') },
    { value: 'ads', label: t('source.ads') },
    { value: 'seo', label: t('source.seo') },
    { value: 'direct', label: t('source.direct') },
    { value: 'referral', label: t('source.referral') },
    { value: 'other', label: t('source.other') },
  ];

  // Form-type filter — bilingual labels, AI option highlighted via emoji
  // to match the badge style used in the list / pipeline / FormsList pages.
  const FORM_TYPE_OPTIONS = [
    { value: 'all',           label: language === 'fr' ? 'Tous les types'      : 'All types' },
    { value: 'standard',      label: language === 'fr' ? 'Standards'           : 'Standard' },
    { value: 'ai_estimation', label: language === 'fr' ? '🤖 IA Estimation'    : '🤖 AI Estimation' },
  ];

  useEffect(() => {
    loadLeads();
  }, [status, source, formType, page]);

  const loadLeads = async () => {
    setIsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (status !== 'all') params.status = status;
      if (source !== 'all') params.source = source;
      if (formType !== 'all') params.form_type = formType;
      if (search) params.search = search;
      
      const response = await leadAPI.list(params);
      setLeads(response.data.leads);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadLeads();
  };

  /**
   * Delete a lead directly from the list. Used as a recovery path when
   * LeadDetailPage cannot render (legacy / corrupted lead with null
   * required fields). Refreshes the list on success.
   */
  const handleDeleteLead = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await leadAPI.delete(deleteTarget.lead_id);
      toast.success(language === 'fr' ? 'Prospect supprimé' : 'Lead deleted');
      setDeleteTarget(null);
      // Also drop it from the local selection set if it was selected.
      setSelectedLeads(prev => {
        if (!prev.has(deleteTarget.lead_id)) return prev;
        const next = new Set(prev);
        next.delete(deleteTarget.lead_id);
        return next;
      });
      loadLeads();
    } catch (err) {
      const code = err.response?.status;
      if (code === 403) {
        toast.error(language === 'fr' ? "Vous n'avez pas la permission." : 'You do not have permission.');
      } else if (code === 404) {
        toast.error(language === 'fr' ? 'Prospect introuvable.' : 'Lead not found.');
        loadLeads();
      } else {
        toast.error(err.response?.data?.detail || t('common.error'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await leadAPI.export();
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads.csv';
      a.click();
    } catch (err) {
      console.error('Error exporting:', err);
    }
  };

  const isValidEmail = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const formatPhone = (v) => {
    const d = v.replace(/[^\d]/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  };
  const isValidPhone = (p) => !p || /^\d{3}-\d{3}-\d{4}$/.test(p);

  const handleAddProspect = async () => {
    if (!addForm.first_name && !addForm.last_name) {
      toast.error(language === 'fr' ? 'Prenom ou nom requis' : 'First or last name required');
      return;
    }
    if (addForm.email && !isValidEmail(addForm.email)) {
      toast.error(language === 'fr' ? 'Format de courriel invalide' : 'Invalid email format');
      return;
    }
    if (addForm.phone && !isValidPhone(addForm.phone)) {
      toast.error(language === 'fr' ? 'Numero de telephone invalide (ex: 514-555-1234)' : 'Invalid phone number');
      return;
    }
    setAddSaving(true);
    try {
      await api.post('/leads', addForm);
      toast.success(language === 'fr' ? 'Prospect ajoute' : 'Prospect added');
      setShowAddProspect(false);
      setAddForm({ first_name: '', last_name: '', email: '', phone: '', address: '', city: '', source: 'direct', status: 'new', notes: '' });
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setAddSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusLabel = (status) => t(`status.${status}`);
  const getSourceLabel = (source) => t(`source.${source}`);

  return (
    <div className="space-y-4" data-testid="leads-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">{t('leads.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('leads.totalCount', { count: total })}
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowAddProspect(true)} data-testid="add-prospect-btn">
            <Plus className="mr-2 h-4 w-4" />
            {language === 'fr' ? 'Ajouter un prospect' : 'Add prospect'}
          </Button>
          <Button asChild variant="outline">
            <Link to="/leads/pipeline">
              {t('leads.pipelineView')}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-csv-btn">
            <Upload className="mr-2 h-4 w-4" />
            {language === 'fr' ? 'Importer CSV' : 'Import CSV'}
          </Button>
          {selectedLeads.size > 0 && (
            <Button onClick={() => setShowBulkEmail(true)} data-testid="bulk-email-btn">
              <Send className="mr-2 h-4 w-4" />
              {language === 'fr' ? `Email (${selectedLeads.size})` : `Email (${selectedLeads.size})`}
            </Button>
          )}
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="mr-2 h-4 w-4" />
            {t('leads.exportCSV')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('leads.searchPlaceholder')}
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary">
                {t('common.search')}
              </Button>
            </form>
            
            <div className="flex gap-2">
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder={t('leads.status')} />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]" data-testid="source-filter">
                  <SelectValue placeholder={t('leads.source')} />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={formType} onValueChange={(v) => { setFormType(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]" data-testid="form-type-filter">
                  <SelectValue placeholder={language === 'fr' ? 'Type' : 'Type'} />
                </SelectTrigger>
                <SelectContent>
                  {FORM_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`form-type-filter-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">{t('leads.noLeadsFound')}</p>
              <p className="text-sm">{t('leads.modifyFilters')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto overscroll-x-contain max-h-[calc(100vh-280px)]">
              <table className="w-full min-w-[920px]">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        className="rounded border-muted-foreground"
                        checked={selectedLeads.size === leads.length && leads.length > 0}
                        onChange={e => {
                          if (e.target.checked) setSelectedLeads(new Set(leads.map(l => l.lead_id)));
                          else setSelectedLeads(new Set());
                        }}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-semibold text-sm">{t('leads.contact')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm">{t('leads.service')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm">{t('leads.city')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm hidden lg:table-cell">{language === 'fr' ? 'Adresse' : 'Address'}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm">{t('leads.status')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm">{t('leads.source')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm">{t('leads.date')}</th>
                    <th className="text-left px-3 py-2 font-semibold text-sm w-20"></th>
                    {canDeleteLead && (
                      <th className="px-3 py-2 font-semibold text-sm text-right w-16 sticky right-0 z-[5] bg-muted/50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">
                        {language === 'fr' ? 'Actions' : 'Actions'}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {leads.map((lead) => (
                    <tr
                      key={lead.lead_id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/leads/${lead.lead_id}`)}
                      data-testid={`lead-row-${lead.lead_id}`}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-muted-foreground"
                          checked={selectedLeads.has(lead.lead_id)}
                          onChange={e => {
                            const next = new Set(selectedLeads);
                            if (e.target.checked) next.add(lead.lead_id);
                            else next.delete(lead.lead_id);
                            setSelectedLeads(next);
                          }}
                          data-testid={`select-lead-${lead.lead_id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="font-bold text-primary">
                              {lead.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold truncate flex items-center gap-2 hover:text-primary hover:underline">
                              {lead.name}
                              {lead.is_urgent && (
                                <AlertCircle className="h-4 w-4 text-red-500" title={t('leads.urgent')} />
                              )}
                              {lead.form_type === 'ai_estimation' && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] border-amber-500/50 text-amber-700 bg-amber-500/10 gap-1 font-semibold whitespace-nowrap"
                                  data-testid={`lead-ai-badge-${lead.lead_id}`}
                                >
                                  <Sparkles className="h-2.5 w-2.5" />
                                  {language === 'fr' ? 'IA Estimation' : 'AI Estimation'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              {lead.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {lead.phone}
                                </span>
                              )}
                              {lead.email && (
                                <span className="flex items-center gap-1 truncate">
                                  <Mail className="h-3 w-3" />
                                  {lead.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-sm">{lead.service || '-'}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-sm flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {lead.city || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        <span className="text-sm truncate max-w-[200px] block">{lead.address || '-'}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge variant="secondary" className={`status-${lead.status}`}>
                          {getStatusLabel(lead.status)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge variant="outline" className={`source-${lead.source}`}>
                          {getSourceLabel(lead.source)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(lead.created_at)}
                        </span>
                      </td>
                      <td className="px-3 py-2 w-20" onClick={(e) => e.stopPropagation()}>
                        <Button asChild variant="ghost" size="sm" data-testid={`view-lead-${lead.lead_id}`}>
                          <Link to={`/leads/${lead.lead_id}`}>
                            {t('common.view')}
                            <ArrowUpRight className="ml-1 h-3 w-3" />
                          </Link>
                        </Button>
                      </td>
                      {canDeleteLead && (
                        <td
                          className="px-3 py-2 text-right w-16 sticky right-0 z-[5] bg-card shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setDeleteTarget({ lead_id: lead.lead_id, name: lead.name || lead.phone || lead.lead_id })}
                            data-testid={`delete-lead-row-${lead.lead_id}`}
                            title={language === 'fr' ? 'Supprimer ce prospect' : 'Delete this lead'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('leads.page', { current: page, total: pages })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t('common.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === pages}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next')}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      {/* Bulk Email Dialog */}
      <EmailComposeDialog
        open={showBulkEmail}
        onClose={() => { setShowBulkEmail(false); setSelectedLeads(new Set()); }}
        isBulk={true}
        leadIds={Array.from(selectedLeads)}
      />

      {/* CSV Import Wizard */}
      <CsvImportWizard
        open={showImport}
        onClose={() => setShowImport(false)}
        onImportComplete={loadLeads}
      />

      {/* Add Prospect Dialog */}
      <Dialog open={showAddProspect} onOpenChange={setShowAddProspect}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Ajouter un prospect' : 'Add prospect'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Prenom' : 'First name'}</Label>
                <Input value={addForm.first_name} onChange={e => setAddForm(f => ({...f, first_name: e.target.value}))} placeholder="Jean" data-testid="prospect-firstname" />
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Nom' : 'Last name'}</Label>
                <Input value={addForm.last_name} onChange={e => setAddForm(f => ({...f, last_name: e.target.value}))} placeholder="Tremblay" data-testid="prospect-lastname" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={addForm.email} onChange={e => setAddForm(f => ({...f, email: e.target.value}))} placeholder="jean@exemple.com" data-testid="prospect-email" className={addForm.email && !isValidEmail(addForm.email) ? 'border-red-500' : ''} />
                {addForm.email && !isValidEmail(addForm.email) && <p className="text-xs text-red-500 mt-1">{language === 'fr' ? 'Format invalide (ex: nom@domaine.com)' : 'Invalid format (e.g. name@domain.com)'}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Telephone' : 'Phone'}</Label>
                <Input value={addForm.phone} onChange={e => setAddForm(f => ({...f, phone: formatPhone(e.target.value)}))} placeholder="514-555-1234" data-testid="prospect-phone" className={addForm.phone && !isValidPhone(addForm.phone) ? 'border-red-500' : ''} />
                {addForm.phone && !isValidPhone(addForm.phone) && <p className="text-xs text-red-500 mt-1">{language === 'fr' ? 'Format: 514-555-1234' : 'Format: 514-555-1234'}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
                <Input value={addForm.address} onChange={e => setAddForm(f => ({...f, address: e.target.value}))} placeholder="123 rue Principale" data-testid="prospect-address" />
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Ville' : 'City'}</Label>
                <Input value={addForm.city} onChange={e => setAddForm(f => ({...f, city: e.target.value}))} placeholder="Montreal" data-testid="prospect-city" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Select value={addForm.source} onValueChange={v => setAddForm(f => ({...f, source: v}))}>
                  <SelectTrigger data-testid="prospect-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maps">Google Maps</SelectItem>
                    <SelectItem value="seo">SEO</SelectItem>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="referral">{language === 'fr' ? 'Reference' : 'Referral'}</SelectItem>
                    <SelectItem value="ads">{language === 'fr' ? 'Publicite' : 'Ads'}</SelectItem>
                    <SelectItem value="other">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{language === 'fr' ? 'Statut' : 'Status'}</Label>
                <Select value={addForm.status} onValueChange={v => setAddForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="prospect-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">{language === 'fr' ? 'Nouveau' : 'New'}</SelectItem>
                    <SelectItem value="contacted">{language === 'fr' ? 'Contacte' : 'Contacted'}</SelectItem>
                    <SelectItem value="appointment">RDV</SelectItem>
                    <SelectItem value="quote_sent">{language === 'fr' ? 'Soumission envoyee' : 'Quote sent'}</SelectItem>
                    <SelectItem value="won">{language === 'fr' ? 'Gagne' : 'Won'}</SelectItem>
                    <SelectItem value="lost">{language === 'fr' ? 'Perdu' : 'Lost'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={addForm.notes} onChange={e => setAddForm(f => ({...f, notes: e.target.value}))} rows={3} placeholder={language === 'fr' ? 'Notes...' : 'Notes...'} data-testid="prospect-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProspect(false)}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleAddProspect} disabled={addSaving || (!addForm.first_name && !addForm.last_name)} data-testid="save-prospect-btn">
              {addSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {language === 'fr' ? 'Ajouter' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-from-list confirmation. Used as a recovery path when the
          dedicated detail page cannot render (legacy / corrupted lead). */}
      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => { if (!open && !isDeleting) setDeleteTarget(null); }}
      >
        <DialogContent data-testid="delete-lead-list-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Supprimer ce prospect ?' : 'Delete this lead?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? `Cette action est irréversible. Le prospect "${deleteTarget?.name ?? ''}" ainsi que ses notes, photos, SMS et historique d'emails seront définitivement supprimés.`
                : `This action cannot be undone. The lead "${deleteTarget?.name ?? ''}" and all its notes, photos, SMS, and email history will be permanently deleted.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
              data-testid="cancel-delete-lead-list-btn"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteLead}
              disabled={isDeleting}
              data-testid="confirm-delete-lead-list-btn"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {language === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
